"use client";

import { useEffect, useMemo, useState } from "react";
import { Button, Paragraph, Title } from "@umahealth/occipital";
import { IUser, IUserBase } from "@/modules/users/domain/IUser";
import { setUserLineas } from "@/modules/users/infraestructure/usersRequests";
import { errorHandler } from "@/config/errorReporting";
import { AdminRoles } from "@/config/adminRoles";

/** "SAN MARTÍN" → "San Martín". */
const titulo = (linea: string) =>
  linea
    .toLowerCase()
    .split(" ")
    .map((p, i) =>
      i && ["de", "la", "del"].includes(p)
        ? p
        : p.charAt(0).toUpperCase() + p.slice(1),
    )
    .join(" ");

/**
 * Qué líneas ve un supervisor de línea en "Indicadores por línea", o un
 * supervisor operativo en Evaluaciones (US 27422).
 *
 * Aparece en el panel de edición sólo si la persona tiene uno de esos roles. La API
 * valida lo mismo que se avisa acá —que tenga el rol y una base, y que la línea
 * exista— y es la que decide: esto sólo arma el pedido y muestra la respuesta.
 */
export default function LineasDeSupervisor({
  user,
  bases,
  onSaved,
}: {
  user: IUser;
  bases: IUserBase[];
  onSaved: () => void;
}) {
  // La línea no es una entidad: es el nombre que comparten sus CRPC.
  const lineasDisponibles = useMemo(
    () =>
      [...new Set(bases.map((b) => b.name).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, "es"),
      ),
    [bases],
  );
  const asignadas = useMemo(
    () => (user.lineas ?? []).map((l) => l.linea),
    [user.lineas],
  );

  const [elegidas, setElegidas] = useState<string[]>(asignadas);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [guardado, setGuardado] = useState(false);

  // Otra persona: se arranca de cero.
  useEffect(() => {
    setError(null);
    setGuardado(false);
  }, [user.uid]);
  // Lo guardado manda: al recargarse el listado después de guardar, la
  // selección queda igual a lo que quedó en la base.
  useEffect(() => {
    setElegidas(asignadas);
  }, [asignadas]);

  const roles = user.roles ?? [];
  const esOperativo = roles.some(
    (r) => r.id === AdminRoles.SUPERVISOR_OPERATIVO,
  );
  const esDeLinea = roles.some((r) => r.id === AdminRoles.SUPERVISOR_LINEA);

  const cambio =
    elegidas.length !== asignadas.length ||
    elegidas.some((l) => !asignadas.includes(l));
  const sinBase = !user.base;

  const alternar = (linea: string) => {
    setGuardado(false);
    setElegidas((actual) =>
      actual.includes(linea)
        ? actual.filter((l) => l !== linea)
        : [...actual, linea],
    );
  };

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      await setUserLineas(user.uid, elegidas);
      setGuardado(true);
      onSaved();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "No se pudieron guardar las líneas.",
      );
      errorHandler?.report(`Error guardando las líneas del supervisor: ${err}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-grey-200 flex flex-col gap-2">
      <Title
        hierarchy="h3"
        size="text-m"
        color="text-primary-800"
        weight="font-semibold"
        className="m-0"
      >
        Líneas que puede ver
      </Title>
      <Paragraph className="text-sm text-grey-500">
        {esOperativo &&
          "Como supervisor operativo, ve e imprime sólo las evaluaciones y troqueles de controles hechos en estas líneas."}
        {esOperativo && esDeLinea && " "}
        {esDeLinea &&
          "Como supervisor de línea, ve sólo Indicadores por línea y sólo estas líneas. No ve la tabla por enfermero."}
      </Paragraph>

      {sinBase && (
        <div className="bg-orange-50 border border-orange-300 rounded-lg p-3 text-sm text-orange-800">
          Asignale una base antes de darle líneas: sin base no puede entrar al
          portal.
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 my-1">
        {lineasDisponibles.map((linea) => (
          <label
            key={linea}
            className="flex items-center gap-2 text-sm text-grey-800 cursor-pointer"
          >
            <input
              type="checkbox"
              checked={elegidas.includes(linea)}
              onChange={() => alternar(linea)}
              disabled={guardando}
              className="accent-primary-700 w-4 h-4"
            />
            {titulo(linea)}
          </label>
        ))}
      </div>

      {elegidas.length === 0 && !sinBase && (
        <p className="text-xs text-orange-700">
          Sin líneas, entra al portal y no ve nada.
        </p>
      )}
      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-3">
          <span className="text-sm text-error-700">{error}</span>
        </div>
      )}
      {guardado && !cambio && (
        <p className="text-xs text-green-700">Líneas guardadas.</p>
      )}

      <Button
        type="button"
        onClick={guardar}
        loading={guardando}
        disabled={guardando || !cambio || (sinBase && elegidas.length > 0)}
        className="w-full bg-primary-700 text-white rounded disabled:opacity-50"
      >
        {guardando ? "Guardando..." : "Guardar líneas"}
      </Button>
    </div>
  );
}
